import React from 'react';
import ReactDOM from 'react-dom';
import { Button } from '@components/atoms/Button';
import { formatForDisplay } from '@utils/formatDate';

export interface SubtaskConflict {
  subtaskId: string;
  subtaskName: string;
  currentStartDate: string;
  currentEndDate: string;
  conflictType: 'before_start' | 'after_end';
}

interface SubtaskConflictModalProps {
  isOpen: boolean;
  parentTaskName: string;
  newParentStartDate: string;
  newParentEndDate: string;
  conflicts: SubtaskConflict[];
  onAdjustSubtasks: () => void;
  onLeaveSubtasks: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  mode: 'move' | 'resize';
}

export const SubtaskConflictModal: React.FC<SubtaskConflictModalProps> = ({
  isOpen,
  parentTaskName,
  newParentStartDate,
  newParentEndDate,
  conflicts,
  onAdjustSubtasks,
  onLeaveSubtasks,
  onCancel,
  isLoading = false,
  mode,
}) => {
  if (!isOpen) return null;

  const isMove = mode === 'move';

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 99999 }}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto relative">
        {/* Header */}
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Subtask Date Conflict
            </h3>
          </div>
        </div>

        {/* Description */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {isMove ? (
              <>
                Moving <strong>"{parentTaskName}"</strong> will cause some
                subtasks to fall outside the new date range.
              </>
            ) : (
              <>
                Resizing <strong>"{parentTaskName}"</strong> will cause some
                subtasks to fall outside the new date range.
              </>
            )}
          </p>

          {/* New Parent Dates */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
              New Parent Task Dates:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700 dark:text-blue-300 font-medium">
                  Start Date:
                </span>
                <br />
                <span className="text-gray-600 dark:text-gray-300">
                  {formatForDisplay(newParentStartDate)}
                </span>
              </div>
              <div>
                <span className="text-blue-700 dark:text-blue-300 font-medium">
                  End Date:
                </span>
                <br />
                <span className="text-gray-600 dark:text-gray-300">
                  {formatForDisplay(newParentEndDate)}
                </span>
              </div>
            </div>
          </div>

          {/* Conflicting Subtasks List */}
          <div className="space-y-3 mb-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Conflicting Subtasks ({conflicts.length}):
            </h4>
            {conflicts.map((conflict) => (
              <div
                key={conflict.subtaskId}
                className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-4 w-4 text-red-400 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="ml-2 flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {conflict.subtaskName}
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                      Currently: {formatForDisplay(conflict.currentStartDate)} -{' '}
                      {formatForDisplay(conflict.currentEndDate)}
                    </p>
                    <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">
                      {conflict.conflictType === 'before_start'
                        ? "Starts before parent's new start date"
                        : "Ends after parent's new end date"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Options */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Choose how to handle these subtasks:
            </h4>

            {/* Option 1: Adjust Subtasks */}
            <button type="button"
              onClick={onAdjustSubtasks}
              disabled={isLoading}
              className="w-full text-left bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={
                      isMove
                        ? 'M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4'
                        : 'M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4'
                    }
                  />
                </svg>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    {isMove
                      ? 'MOVE SUBTASKS WITH PARENT'
                      : 'ADJUST SUBTASKS TO FIT'}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-300 mt-1">
                    {isMove
                      ? 'Subtasks will be shifted by the same number of days, maintaining relative positions within the parent task.'
                      : 'Subtasks will be adjusted to fit within the new parent task dates. Dates outside the range will be truncated.'}
                  </p>
                </div>
              </div>
            </button>

            {/* Option 2: Leave Subtasks */}
            <button type="button"
              onClick={onLeaveSubtasks}
              disabled={isLoading}
              className="w-full text-left bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <div className="ml-3">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    LEAVE SUBTASKS AT CURRENT DATES
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">
                    {isMove
                      ? 'Move only the parent task. Subtasks will remain at their current dates (outside parent range).'
                      : 'Resize only the parent task. Subtasks will remain at their current dates (may be outside parent range).'}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Cancel Button */}
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 dark:bg-gray-800 dark:bg-opacity-75 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-600 border-t-transparent"></div>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Updating tasks...
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
